export const CASE_STUDY_SECTION_TYPES = [
  'text',
  'list',
  'diagram',
  'gallery',
  'timeline',
  'technologies',
  'metrics',
  'code',
]

const VALID_SECTION_TYPES = new Set(CASE_STUDY_SECTION_TYPES)

export const EMPTY_CASE_STUDY = {
  hero: null,
  sections: [],
}

export const parseCaseStudyValue = (raw = {}) => {
  const value = raw.case_study ?? raw.caseStudy

  if (value == null) {
    return null
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return null
    }
  }

  return typeof value === 'object' ? value : null
}

const parseHero = (hero) => {
  if (!hero || typeof hero !== 'object') {
    return null
  }

  const title = hero.title?.trim() || undefined
  const subtitle = hero.subtitle?.trim() || undefined
  const cover = hero.cover?.trim() || undefined

  if (!title && !subtitle && !cover) {
    return null
  }

  return { title, subtitle, cover }
}

const normalizeTextContent = (content) => {
  if (typeof content !== 'string' || !content.trim()) {
    return null
  }

  return content.trim()
}

const normalizeListContent = (content) => {
  if (!Array.isArray(content)) {
    return null
  }

  const items = content.filter((item) => typeof item === 'string' && item.trim())

  return items.length ? items : null
}

const normalizeDiagramContent = (content) => {
  if (!content || typeof content !== 'object') {
    return null
  }

  const image = content.image?.trim()

  if (!image) {
    return null
  }

  return {
    image,
    caption: content.caption?.trim() || undefined,
  }
}

const normalizeGalleryContent = (content) => {
  if (!Array.isArray(content)) {
    return null
  }

  const items = content
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      image: item.image?.trim() || '',
      caption: item.caption?.trim() || undefined,
    }))
    .filter((item) => item.image)

  return items.length ? items : null
}

const normalizeTimelineContent = (content) => {
  if (!Array.isArray(content)) {
    return null
  }

  const items = content
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      title: item.title?.trim() || '',
      description: item.description?.trim() || '',
    }))
    .filter((item) => item.title || item.description)

  return items.length ? items : null
}

const normalizeTechnologiesContent = (content) => {
  if (!Array.isArray(content)) {
    return null
  }

  const items = content
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      name: item.name?.trim() || '',
      category: item.category?.trim() || undefined,
    }))
    .filter((item) => item.name)

  return items.length ? items : null
}

const normalizeCodeContent = (content) => {
  if (typeof content === 'string') {
    const code = content.trim()
    return code ? { code, language: undefined } : null
  }

  if (!content || typeof content !== 'object') {
    return null
  }

  const code = (content.code ?? content.content)?.trim()

  if (!code) {
    return null
  }

  return {
    code,
    language: content.language?.trim() || undefined,
  }
}

const normalizeSectionContent = (type, content) => {
  switch (type) {
    case 'text':
      return normalizeTextContent(content)
    case 'list':
    case 'metrics':
      return normalizeListContent(content)
    case 'diagram':
      return normalizeDiagramContent(content)
    case 'gallery':
      return normalizeGalleryContent(content)
    case 'timeline':
      return normalizeTimelineContent(content)
    case 'technologies':
      return normalizeTechnologiesContent(content)
    case 'code':
      return normalizeCodeContent(content)
    default:
      return null
  }
}

const normalizeSection = (section, index) => {
  if (!section || typeof section !== 'object') {
    return null
  }

  const type = section.type?.trim()

  if (!type || !VALID_SECTION_TYPES.has(type)) {
    return null
  }

  const content = normalizeSectionContent(type, section.content)

  if (content == null) {
    return null
  }

  return {
    id: section.id?.trim() || `section-${index}`,
    order: typeof section.order === 'number' ? section.order : index,
    type,
    title: section.title?.trim() || undefined,
    content,
  }
}

/**
 * Parses case_study JSON into { hero, sections }.
 * Expects { hero?: {...}, sections: [{ id, order, type, title, content }] }.
 */
export const parseCaseStudy = (raw = {}) => {
  const parsed = parseCaseStudyValue(raw)

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ...EMPTY_CASE_STUDY }
  }

  const sections = (Array.isArray(parsed.sections) ? parsed.sections : [])
    .map(normalizeSection)
    .filter(Boolean)
    .sort((a, b) => a.order - b.order)

  return {
    hero: parseHero(parsed.hero),
    sections,
  }
}
